<script setup lang="ts">
import { Slot as RadixSlot } from 'radix-vue';

import type { FormFieldProps, FormFieldSlots } from '@/components/formField/FormField';

import { BadgeSize, BadgeVariant } from '@/hooks/useBadgeClasses';
import { useFormField } from '@/components/formField/useFormField';

import Badge from '@/components/badge/Badge.vue';
import FormError from '@/components/formError/FormError.vue';
import FormHelperText from '@/components/formHelperText/FormHelperText.vue';
import Label from '@/components/label/Label.vue';
import VerticalDivider from '@/components/verticalDivider/VerticalDivider.vue';

import HistoryAltIcon from '@/icons/line/history-alt.svg';

const props = withDefaults(
  defineProps<
    FormFieldProps & {
      isFieldset?: boolean;
    }
  >(),
  {
    inheritId: true,
    hasError: false
  }
);

defineSlots<
  FormFieldSlots & {
    default?: (props: { id: string }) => unknown;
  }
>();

const { id } = useFormField({ props });
</script>

<template>
  <component :is="isFieldset ? 'fieldset' : 'div'" class="w-full min-w-0 space-y-1.5">
    <div>
      <Label
        :as="isFieldset ? 'legend' : 'label'"
        class="block leading-4"
        :class="{ 'opacity-50': isDisabled }"
        :for="id"
        :hasError="hasError"
      >
        <slot name="label">
          <div class="flex items-center gap-x-1.5">
            <div class="flex w-full justify-between gap-x-1.5 overflow-hidden">
              <div class="flex items-center gap-x-1.5">
                <span>{{ label }}</span>
                <template v-if="isOptional">
                  <VerticalDivider size="sm" />
                  <span class="font-normal italic text-slate-500">Optional</span>
                </template>
              </div>
              <Badge
                v-if="previousValue !== undefined && previousValue !== null && previousValue !== ''"
                :iconLeft="HistoryAltIcon"
                :label="`Was: ${previousValue}`"
                :variant="BadgeVariant.soft"
                :size="BadgeSize.xs"
                class="shrink-0"
              />
              <Badge
                v-if="
                  statusBadgeLabel !== undefined &&
                  statusBadgeLabel !== null &&
                  statusBadgeLabel !== ''
                "
                :color="statusBadgeColor"
                :iconLeft="statusBadgeIcon"
                :label="statusBadgeLabel"
                :variant="BadgeVariant.soft"
                :size="BadgeSize.xs"
                class="shrink-0"
              />
            </div>

            <slot name="info" />
          </div>
        </slot>
      </Label>
    </div>

    <RadixSlot :id="inheritId ? id : undefined">
      <slot :id="id" />
    </RadixSlot>

    <slot name="helperText" />
    <FormHelperText v-if="helperText && !error" :class="{ 'opacity-50': isDisabled }">
      {{ helperText }}
    </FormHelperText>

    <slot name="error" />
    <FormError v-if="error">{{ error }}</FormError>
  </component>
</template>
